/**
 * Frame-aligned event batching (SPEC §10: "deltas batched via
 * `requestAnimationFrame` ≤16 Hz").
 *
 * Every stream event goes through here, not just `text.delta`. Batching only
 * the deltas would let a `tool.start` overtake buffered text and render the
 * transcript out of order; batching the whole queue preserves wire order and
 * costs at most one flush interval (~62 ms) of latency on the other events.
 *
 * The clock and the scheduler are injectable so the throttle can be tested
 * deterministically — a real rAF fires ~60 Hz and would make "≤16 Hz" a timing
 * race in jsdom.
 */

export interface BatcherOptions {
  /** Upper bound on flushes per second. Default 16. */
  readonly maxHz?: number;
  readonly now?: () => number;
  readonly schedule?: (callback: () => void) => number;
  readonly cancelScheduled?: (handle: number) => void;
  readonly setTimer?: (callback: () => void, ms: number) => number;
  readonly clearTimer?: (handle: number) => void;
}

function defaultSchedule(callback: () => void): number {
  if (typeof requestAnimationFrame === 'function') return requestAnimationFrame(() => callback());
  return setTimeout(callback, 16) as unknown as number;
}

function defaultCancel(handle: number): void {
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(handle);
  else clearTimeout(handle);
}

export class EventBatcher<T> {
  private queue: T[] = [];
  private lastFlushAt = Number.NEGATIVE_INFINITY;
  private rafHandle: number | null = null;
  private timerHandle: number | null = null;
  private disposed = false;

  private readonly intervalMs: number;
  private readonly now: () => number;
  private readonly schedule: (callback: () => void) => number;
  private readonly cancelScheduled: (handle: number) => void;
  private readonly setTimer: (callback: () => void, ms: number) => number;
  private readonly clearTimer: (handle: number) => void;

  constructor(
    private readonly onFlush: (batch: T[]) => void,
    options: BatcherOptions = {},
  ) {
    this.intervalMs = 1000 / (options.maxHz ?? 16);
    this.now = options.now ?? (() => Date.now());
    this.schedule = options.schedule ?? defaultSchedule;
    this.cancelScheduled = options.cancelScheduled ?? defaultCancel;
    this.setTimer =
      options.setTimer ?? ((callback, ms) => setTimeout(callback, ms) as unknown as number);
    this.clearTimer = options.clearTimer ?? ((handle) => clearTimeout(handle));
  }

  push(item: T): void {
    if (this.disposed) return;
    this.queue.push(item);
    this.ensureScheduled();
  }

  pushAll(items: readonly T[]): void {
    if (this.disposed || items.length === 0) return;
    this.queue.push(...items);
    this.ensureScheduled();
  }

  get pending(): number {
    return this.queue.length;
  }

  /** Flush immediately, ignoring the throttle (unmount, tab hide, tests). */
  flushNow(): void {
    this.clearHandles();
    this.flush();
  }

  dispose(): void {
    this.clearHandles();
    this.queue = [];
    this.disposed = true;
  }

  private ensureScheduled(): void {
    if (this.rafHandle !== null || this.timerHandle !== null) return;
    const wait = this.intervalMs - (this.now() - this.lastFlushAt);
    if (wait > 0) {
      this.timerHandle = this.setTimer(() => {
        this.timerHandle = null;
        this.onTick();
      }, wait);
    } else {
      this.rafHandle = this.schedule(() => {
        this.rafHandle = null;
        this.onTick();
      });
    }
  }

  private onTick(): void {
    if (this.disposed) return;
    if (this.now() - this.lastFlushAt + 1e-9 < this.intervalMs) {
      // Woken early (a coalesced timer, or a rAF that beat the interval).
      this.ensureScheduled();
      return;
    }
    this.flush();
    if (this.queue.length > 0) this.ensureScheduled();
  }

  private flush(): void {
    if (this.queue.length === 0) return;
    this.lastFlushAt = this.now();
    const batch = this.queue;
    this.queue = [];
    this.onFlush(batch);
  }

  private clearHandles(): void {
    if (this.rafHandle !== null) {
      this.cancelScheduled(this.rafHandle);
      this.rafHandle = null;
    }
    if (this.timerHandle !== null) {
      this.clearTimer(this.timerHandle);
      this.timerHandle = null;
    }
  }
}
